<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import { storeToRefs } from "pinia";
import { ref, type Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { VALIDATION_REGEXP } from "~/core/constants/constants";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { eventBus } from "~/core/services/eventBus";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useCreateSector } from "~/modules/sector/composables/useCreateSector";
import { useFetchSectors } from "~/modules/sector/composables/useFetchSectors";
import { useUpdateSector } from "~/modules/sector/composables/useUpdateSector";
import {
  SECTOR_DESCRIPTION_MAX_LENGTH,
  SECTOR_NAME_MAX_LENGTH,
  SECTOR_NAME_MIN_LENGTH,
} from "~/modules/sector/constants";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorCreatePayload } from "~/modules/sector/models/SectorCreatePayload.interface";
import type { SectorUpdatePayload } from "~/modules/sector/models/SectorUpdatePayload.interface";

const serialize = useSerialize();

const props = defineProps<{
  /**
   * True of false to display the form.
   */
  show: boolean;

  /**
   * Code of the parent sector.
   */
  parentSectorCode?: number;

  /**
   * Sector to update if provided.
   * If undefined, the form is in creation mode.
   */
  sector?: Sector;
}>();

const emit = defineEmits(["update:show", "change", "close"]);

const { t } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { fetchSectors } = useFetchSectors(currentOrganization.value?.code);

const {
  isLoading: isLoadingCreateSector,
  error: errorCreateSector,
  createSector,
  result: resultCreateSector,
} = useCreateSector(currentOrganization.value?.code);

const {
  isLoading: isLoadingUpdateSector,
  error: errorUpdateSector,
  updateSector,
} = useUpdateSector(currentOrganization.value?.code);

const { can } = useAcl();

const canUpdateSector = computed(
  () =>
    !props.sector?.isRootSector &&
    can(ACLRoles.CAN_WRITE_SECTOR) &&
    (!props.sector || props.sector?.statuses.granted),
);

const sideCanvasHeader = computed(() => {
  if (!canUpdateSector.value) {
    return t("sectors.modals.view.title");
  }
  return props.sector
    ? t("sectors.modals.update.title")
    : t("sectors.modals.create.title");
});

const sectorFormRef = ref();
const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();
const sectorForm = ref(getForm());
const initFormStringified = ref(serialize(sectorForm.value));

const rules = reactive({
  name: [
    {
      required: true,
      message: t("sectors.validation.required"),
      whitespace: true,
    },
    {
      min: SECTOR_NAME_MIN_LENGTH,
      message: t("sectors.validation.minLength", {
        minLength: SECTOR_NAME_MIN_LENGTH,
      }),
    },
    {
      max: SECTOR_NAME_MAX_LENGTH,
      message: t("sectors.validation.maxLength", {
        maxLength: SECTOR_NAME_MAX_LENGTH,
      }),
    },
    {
      pattern: VALIDATION_REGEXP.ASCII_EXTENDED,
      message: t("input.error.invalidFormat"),
    },
  ],
  description: [
    {
      max: SECTOR_DESCRIPTION_MAX_LENGTH,
      message: t("sectors.validation.maxLength", {
        maxLength: SECTOR_DESCRIPTION_MAX_LENGTH,
      }),
    },
    {
      pattern: VALIDATION_REGEXP.ASCII_EXTENDED,
      message: t("input.error.invalidFormat"),
    },
  ],
});

const showSideCanvas: WritableComputedRef<boolean> = computed({
  get: () => {
    return props.show;
  },
  set: (shown: boolean) => {
    emit("update:show", shown);
  },
});

const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => sectorFormRef.value?.isValid,
);

const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(sectorForm.value) !== initFormStringified.value,
);

function getForm(sector?: Sector) {
  return {
    name: sector?.name || "",
    description: sector?.description || "",
  };
}

async function submit() {
  const isFormValid = await sectorFormRef.value.validate().catch(() => false);

  if (props.parentSectorCode && isFormValid) {
    // Update the sector
    if (props.sector) {
      const sectorUpdatePayload: SectorUpdatePayload = {
        code: props.sector.code,
        parentCode: props.parentSectorCode,
        ...sectorForm.value,
      };
      await updateSector(props.sector.code, sectorUpdatePayload);

      if (errorUpdateSector.value) {
        errorUpdateSector.value.notify(NotificationKey.saveError);
      } else {
        const parentSectorCode = props.parentSectorCode;
        const sectorCode = props.sector.code;

        emit("change");

        showNotificationSuccess(t(NotificationKey.saveSuccess));

        await fetchSectors(true);

        eventBus.emit("sector", {
          parentSectorCode,
          sectorCode,
        });
      }
    }
    // Create a sector
    else {
      const newSector: SectorCreatePayload = {
        parentCode: props.parentSectorCode,
        ...sectorForm.value,
      };
      await createSector(newSector);

      if (errorCreateSector.value) {
        errorCreateSector.value.notify(NotificationKey.saveError);
      } else {
        const parentSectorCode = props.parentSectorCode;
        const sectorCode = resultCreateSector.value?.code;

        emit("change");

        showNotificationSuccess(t(NotificationKey.saveSuccess));

        await fetchSectors(true);

        eventBus.emit("sector", {
          parentSectorCode,
          sectorCode,
        });
      }
    }
  }
}

function onCancelClick() {
  sideCanvasRef.value?.hide();
}

watch(
  () => [props.sector, props.show] as const,
  ([sector]) => {
    sectorForm.value = getForm(sector);
    initFormStringified.value = serialize(sectorForm.value);
    setTimeout(sectorFormRef.value.clearValidate, 0);
  },
);
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasRef"
    v-model:show="showSideCanvas"
    :header="sideCanvasHeader"
    :confirm-title="t('message.confirm.cancel.title')"
    :confirm-message="t('message.confirm.cancel.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    @hidden="emit('close')"
  >
    <lxc-form ref="sectorFormRef" :model="sectorForm" :rules="rules">
      <lxc-form-item
        :label="t('sectors.modals.create.sector.label')"
        prop="name"
        :disabled="!canUpdateSector"
      >
        <lxc-input
          v-model="sectorForm.name"
          type="text"
          :min-length="SECTOR_NAME_MIN_LENGTH"
          :max-length="SECTOR_NAME_MAX_LENGTH"
        />
      </lxc-form-item>
      <lxc-form-item
        :label="t('sectors.modals.create.sector.description')"
        prop="description"
        :disabled="!canUpdateSector"
      >
        <lxc-text-area
          v-model="sectorForm.description"
          :max-length="SECTOR_DESCRIPTION_MAX_LENGTH"
        />
      </lxc-form-item>
    </lxc-form>

    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="onCancelClick"
        />
        <lxc-button
          v-if="canUpdateSector"
          html-type="button"
          type="primary"
          class="grow"
          :text="t('button.confirm')"
          :title="t('button.confirm')"
          :disabled="!isFormValid || !formHasChanged"
          :is-loading="isLoadingCreateSector || isLoadingUpdateSector"
          @click="submit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
